import { ChevronDown, ChevronRight } from "lucide-react";
import React from "react";

import { TableCell, TableRow } from "@/components/ui/table";
import { formatRuDate } from "@/lib/format";
import {
  CELL_PAD,
  NUM_CELL,
  formatRubCompact,
  groupEntriesByWeek,
  hasNonZeroAmount,
  isZeroAmount,
  stickyCell,
  sumDecimalStrings,
  undistributedCells,
  type CashflowEntryGroup,
} from "@/pages/cashflow/utils";
import type { CashflowEntry } from "@/types/api";

/** Подпись группы, когда контрагента у записи нет (ручные строки, налоги). */
const NO_KONTRAGENT = "Без контрагента";

/** Ключ раскрытия контрагента внутри статьи. */
function kontragentKey(category: string, kontragent: string): string {
  return `${category}|${kontragent}`;
}

/** Строка дерева: подписи с отступом уровня, ячейки без кликов и без подсветки минуса. */
function TreeRow({
  label,
  meta,
  indent,
  cells,
  total,
  weeks,
  expandable,
  danger = false,
}: {
  label: string;
  /** Мелкая приписка справа от подписи (дата документа). */
  meta?: string;
  indent: string;
  cells: (string | undefined)[];
  total: string | undefined;
  weeks: string[];
  /** Строка раскрывается дальше (уровень контрагента). */
  expandable?: { expanded: boolean; onToggle: () => void };
  danger?: boolean;
}): React.ReactElement {
  const Chevron = expandable?.expanded === true ? ChevronDown : ChevronRight;
  const rowCls = `group hover:bg-muted ${danger ? "text-rose-700" : "text-muted-foreground"}`;
  return (
    <TableRow className={rowCls}>
      <TableCell className={`${stickyCell("left", false)} ${CELL_PAD} ${indent}`}>
        {expandable === undefined ? (
          <span>{label}</span>
        ) : (
          <button
            type="button"
            aria-expanded={expandable.expanded}
            aria-label={`${expandable.expanded ? "Свернуть" : "Раскрыть"} ${label}`}
            onClick={expandable.onToggle}
            className="inline-flex items-center gap-1 rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Chevron className="h-3 w-3 shrink-0" aria-hidden="true" />
            {label}
          </button>
        )}
        {meta !== undefined && <span className="ml-2 text-[10px]">{meta}</span>}
      </TableCell>
      {weeks.map((w, i) => (
        <TableCell
          key={w}
          className={`${NUM_CELL} ${CELL_PAD} ${isZeroAmount(cells[i]) ? "opacity-50" : ""}`}
        >
          {formatRubCompact(cells[i])}
        </TableCell>
      ))}
      <TableCell className={`${NUM_CELL} ${CELL_PAD} ${stickyCell("right", false)}`}>
        {formatRubCompact(total)}
      </TableCell>
    </TableRow>
  );
}

/** Документы внутри контрагента: подпись `doc ?? title` + дата записи. */
function DocRows({
  group,
  weeks,
}: {
  group: CashflowEntryGroup;
  weeks: string[];
}): React.ReactElement {
  const docs = groupEntriesByWeek(group.entries, weeks, (e) => e.doc ?? e.title);
  return (
    <>
      {docs.map((doc) => (
        <TreeRow
          key={doc.key}
          label={doc.label}
          meta={formatRuDate(
            doc.entries.map((e) => e.date).reduce((a, b) => (a <= b ? a : b)),
          )}
          indent="pl-10"
          cells={doc.cells}
          total={doc.total}
          weeks={weeks}
        />
      ))}
    </>
  );
}

interface Props {
  category: string;
  /** Ячейки строки статьи — с ними сверяется сумма подстрок. */
  rowCells: string[];
  weeks: string[];
  /** Записи ЭТОЙ статьи и вкладки организации (`utils.filterEntries`). */
  entries: CashflowEntry[];
  /** Раскрытые контрагенты (ключи `kontragentKey`). */
  expanded: Set<string>;
  onToggle: (key: string) => void;
}

/**
 * Подстроки статьи: контрагент → документ.
 *
 * Если сумма контрагентов не сходится со строкой статьи (запись вне горизонта,
 * рассинхрон с бэком) — внизу появляется строка «не разнесено», а не тихое
 * расхождение.
 */
export function CalendarTreeRows({
  category,
  rowCells,
  weeks,
  entries,
  expanded,
  onToggle,
}: Props): React.ReactElement {
  const groups = groupEntriesByWeek(entries, weeks, (e) => e.kontragent ?? NO_KONTRAGENT);
  const rest = undistributedCells(rowCells, groups, weeks);
  const restTotal = sumDecimalStrings(rest);

  return (
    <>
      {groups.map((g) => {
        const key = kontragentKey(category, g.key);
        const isOpen = expanded.has(key);
        return (
          <React.Fragment key={key}>
            <TreeRow
              label={g.label}
              indent="pl-6"
              cells={g.cells}
              total={g.total}
              weeks={weeks}
              expandable={{ expanded: isOpen, onToggle: () => onToggle(key) }}
            />
            {isOpen && <DocRows group={g} weeks={weeks} />}
          </React.Fragment>
        );
      })}
      {hasNonZeroAmount(rest) && (
        <TreeRow
          label={`не разнесено: ${formatRubCompact(restTotal)}`}
          indent="pl-6"
          cells={rest}
          total={restTotal}
          weeks={weeks}
          danger
        />
      )}
    </>
  );
}
